import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { SentimentSatisfied, SentimentDissatisfied, SentimentNeutral } from '@mui/icons-material';
import type { NoteFeedbackEvent2 } from '@store/generated/admin';
import { formatDateTime } from '../utils/formatting';

interface NoteFeedbackDisplayProps {
  event: NoteFeedbackEvent2;
}

export function NoteFeedbackDisplay({ event }: NoteFeedbackDisplayProps) {
  // Map rating to icon and label
  const getRatingDisplay = () => {
    switch (event.note_rating) {
      case 0:
        return {
          icon: <SentimentSatisfied />,
          label: 'Satisfied',
          color: 'success' as const,
          borderColor: 'success.main',
        };
      case 1:
        return {
          icon: <SentimentDissatisfied />,
          label: 'Dissatisfied',
          color: 'error' as const,
          borderColor: 'error.main',
        };
      case 2:
        return {
          icon: <SentimentNeutral />,
          label: 'Somewhat',
          color: 'warning' as const,
          borderColor: 'warning.main',
        };
      default:
        return {
          icon: <SentimentNeutral />,
          label: 'Unknown',
          color: 'default' as const,
          borderColor: 'grey.500',
        };
    }
  };

  const ratingDisplay = getRatingDisplay();

  return (
    <Card
      sx={{
        mb: 2,
        borderLeft: 4,
        borderLeftColor: ratingDisplay.borderColor,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Chip
            icon={ratingDisplay.icon}
            label={ratingDisplay.label}
            color={ratingDisplay.color}
            size="small"
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(new Date(event.timestamp * 1000).toISOString())}
          </Typography>
        </Box>

        {/* Comment Section */}
        {event.note_comment && (
          <Box>
            <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
              Comment:
            </Typography>
            <Typography variant="body2" fontStyle="italic" color="text.secondary">
              {event.note_comment}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
